import { useEffect, useState } from "react";
import api, { errMsg } from "../api";
import { MODULES } from "../utils";
import { toast } from "sonner";

function ExportSection() {
  const [busy, setBusy] = useState(false);

  const doExport = async () => {
    setBusy(true);
    try {
      const r = await api.get("/export", { responseType: "blob" });
      const m = (r.headers["content-disposition"] || "").match(/filename=([^;]+)/);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1].trim() : "Saphir_Immo_export.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-lg border border-slate-200 p-6 space-y-3" data-testid="settings-export-section">
      <h2 className="text-sm font-semibold text-slate-900">Sauvegarde — export des données</h2>
      <p className="text-xs text-slate-500">
        Un fichier Excel (.xlsx) avec un onglet par table : clients, bons de commande, lignes des bons,
        factures, lignes de facture, liste de prix, utilisateurs, configuration. Les photos de reçus
        ne sont pas incluses (une colonne indique seulement si un reçu existe). Chaque export est
        enregistré dans le journal d'activité.
      </p>
      <button data-testid="settings-export-button" onClick={doExport} disabled={busy}
        className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-50">
        {busy ? "Export en cours…" : "Exporter les données"}
      </button>
    </section>
  );
}

function TwoFactorSection() {
  const [st, setSt] = useState(null);
  const [step, setStep] = useState("idle"); // idle | test-sent | codes
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [noted, setNoted] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/security/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!st) return null;

  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post("/security/2fa/test-code");
      setStep("test-sent");
      toast.success("Code de test envoyé à votre adresse email");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await api.post("/security/2fa/enable", { code });
      setCodes(r.data.backup_codes);
      setStep("codes");
      setNoted(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const confirmActivation = async () => {
    setBusy(true);
    try {
      await api.post("/security/2fa/confirm");
      toast.success("Code de connexion par email activé pour tous les comptes");
      setStep("idle"); setCodes(null); setCode("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Désactiver le code de connexion par email pour tous les comptes ?")) return;
    try {
      await api.post("/security/2fa/disable");
      toast.success("Code par email désactivé");
      setStep("idle"); setCodes(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const regen = async () => {
    if (!window.confirm("Régénérer 10 nouveaux codes de secours ? Les anciens seront invalidés.")) return;
    try {
      const r = await api.post("/security/backup-codes");
      setCodes(r.data.backup_codes);
      setStep("codes");
      setNoted(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copy = () => {
    navigator.clipboard.writeText(codes.join("\n"));
    toast.success("Codes copiés dans le presse-papiers");
  };

  return (
    <section className="bg-white rounded-lg border border-slate-200 p-6 space-y-4" data-testid="settings-2fa-section">
      <h2 className="text-sm font-semibold text-slate-900">Sécurité — code de connexion par email</h2>
      <p className="text-sm text-slate-600">
        Statut : {st.enabled
          ? <span className="font-semibold text-emerald-600" data-testid="settings-2fa-status">Activé</span>
          : <span className="font-semibold text-slate-500" data-testid="settings-2fa-status">Désactivé</span>}
        {st.configured && <span className="text-xs text-slate-500" data-testid="settings-2fa-remaining"> — codes de secours restants : {st.backup_codes_remaining}</span>}
      </p>
      {st.force_off && (
        <p className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="settings-2fa-force-off">
          La désactivation est forcée par la variable d'environnement (interrupteur de secours) : le code par email reste inactif quelle que soit cette configuration.
        </p>
      )}
      {!st.resend_configured && (
        <p className="text-xs bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2" data-testid="settings-2fa-no-resend">
          La clé Resend est absente : le code de connexion par email ne peut pas être activé.
        </p>
      )}

      {!st.configured && step === "idle" && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">L'activation se fait en deux étapes : un code de test est d'abord envoyé à votre adresse email pour vérifier que l'envoi fonctionne, puis 10 codes de secours vous sont présentés une seule fois.</p>
          <button data-testid="settings-2fa-send-test" onClick={sendTest} disabled={busy || !st.resend_configured || st.force_off}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-50">
            Activer le code de connexion par email
          </button>
        </div>
      )}

      {!st.configured && step === "test-sent" && (
        <div className="space-y-3" data-testid="settings-2fa-test-step">
          <p className="text-xs text-slate-500">Saisissez le code de test reçu par email (valable 10 minutes).</p>
          <div className="flex items-center gap-2">
            <input data-testid="settings-2fa-test-code-input" value={code} onChange={(e) => setCode(e.target.value)}
              inputMode="numeric" maxLength={6} placeholder="000000"
              className="w-36 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono tracking-[0.3em] text-center" />
            <button data-testid="settings-2fa-enable-button" onClick={enable} disabled={busy || code.trim().length === 0}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-50">
              Valider le code de test
            </button>
            <button data-testid="settings-2fa-test-resend" onClick={sendTest} disabled={busy}
              className="px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">
              Renvoyer
            </button>
          </div>
        </div>
      )}

      {step === "codes" && codes && (
        <div className="space-y-3" data-testid="settings-2fa-backup-codes">
          <p className="text-xs font-semibold text-slate-700">Codes de secours — affichés une seule fois. Notez-les et conservez-les en lieu sûr (chacun utilisable une seule fois, à la place du code email).</p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm bg-slate-50 rounded-lg p-4" data-testid="settings-2fa-codes-list">
            {codes.map((c) => <li key={c}>{c}</li>)}
          </ul>
          <div className="flex items-center gap-3 flex-wrap">
            <button data-testid="settings-2fa-copy" onClick={copy}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
              Copier les codes
            </button>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="settings-2fa-noted" type="checkbox" checked={noted} onChange={(e) => setNoted(e.target.checked)} />
              Je les ai notés
            </label>
            <button data-testid="settings-2fa-finish" onClick={st.configured ? () => { setStep("idle"); setCodes(null); load(); } : confirmActivation}
              disabled={!noted || busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-50">
              {st.configured ? "Terminer" : "Terminer et activer"}
            </button>
          </div>
        </div>
      )}

      {st.configured && step === "idle" && (
        <div className="flex gap-2 flex-wrap">
          <button data-testid="settings-2fa-regenerate" onClick={regen}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
            Régénérer les codes de secours
          </button>
          <button data-testid="settings-2fa-disable" onClick={disable}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50">
            Désactiver le code par email
          </button>
        </div>
      )}
    </section>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="p-8 text-slate-500" data-testid="settings-loading">Chargement…</div>;

  const set = (k, v) => setS({ ...s, [k]: v });
  const setCompte = (m, v) => setS({ ...s, comptes_comptables: { ...s.comptes_comptables, [m]: v } });

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        company_name: s.company_name, company_street: s.company_street, company_zip: s.company_zip,
        company_city: s.company_city, company_country: s.company_country, company_email: s.company_email,
        company_phone: s.company_phone, iban: s.iban, clearing: s.clearing, bic_swift: s.bic_swift,
        bank_name: s.bank_name, bank_address: s.bank_address, tva_rate: parseFloat(s.tva_rate) || 0,
        tva_number: s.tva_number || "", price_modes: s.price_modes, invoice_prefix: s.invoice_prefix, order_prefix: s.order_prefix,
        compte_tiers_default: s.compte_tiers_default, comptes_comptables: s.comptes_comptables,
        next_winbiz_number: (s.next_winbiz_number === "" || s.next_winbiz_number == null) ? null : parseInt(s.next_winbiz_number, 10),
        winbiz_vat_code: s.winbiz_vat_code || "300",
        pricing: Object.fromEntries(Object.entries(s.pricing).map(([k, v]) => [k, parseFloat(v) || 0])),
      };
      await api.put("/settings", payload);
      toast.success("Configuration enregistrée");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const F = ({ label, k, type = "text", testid }) => (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      <input data-testid={testid || `settings-${k}`} type={type} value={s[k] ?? ""} step={type === "number" ? "0.01" : undefined}
        onChange={(e) => set(k, e.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:outline-none" />
    </div>
  );

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5" data-testid="config-page">
      <h1 className="text-xl font-semibold text-slate-900">Configuration</h1>
      <form onSubmit={save} className="space-y-6">
        <section className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-slate-900">Entreprise</h2>
          <div className="grid grid-cols-2 gap-3">
            <F label="Raison sociale" k="company_name" />
            <F label="Email" k="company_email" />
          </div>
          <F label="Adresse et numéro" k="company_street" />
          <div className="grid grid-cols-3 gap-3">
            <F label="Code postal" k="company_zip" />
            <F label="Ville" k="company_city" />
            <F label="Pays" k="company_country" />
          </div>
          <F label="Téléphone" k="company_phone" />
        </section>

        <section className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-slate-900">Coordonnées bancaires</h2>
          <F label="IBAN" k="iban" testid="settings-iban" />
          <div className="grid grid-cols-2 gap-3">
            <F label="Clearing" k="clearing" testid="settings-clearing" />
            <F label="BIC / SWIFT" k="bic_swift" testid="settings-bic" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Nom de la banque" k="bank_name" testid="settings-bank-name" />
            <F label="Adresse de la banque" k="bank_address" testid="settings-bank-address" />
          </div>
        </section>

        <section className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-slate-900">TVA & prix</h2>
          <div className="grid grid-cols-2 gap-3">
            <F label="Taux de TVA (%)" k="tva_rate" type="number" testid="settings-tva-rate" />
            <F label="N° TVA (ex : CHE-123.456.789 TVA)" k="tva_number" testid="settings-tva-number" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Prix saisis HT / TTC par secteur</label>
            <div className="grid grid-cols-3 gap-3 mt-1">
              {Object.entries(MODULES).map(([m, M]) => (
                <div key={m}>
                  <label className="text-[11px] font-semibold" style={{ color: M.color }}>{M.label}</label>
                  <select data-testid={`settings-price-mode-${m}`} value={s.price_modes?.[m] || "TTC"}
                    onChange={(e) => set("price_modes", { ...s.price_modes, [m]: e.target.value })}
                    className="mt-0.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                    <option value="TTC">TTC (taxes comprises)</option>
                    <option value="HT">HT (hors taxes)</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500">Les tarifs de chaque secteur se gèrent dans « Liste des prix » — une liste par module (pièce, kg, heure ou forfait).</p>
        </section>

        <section className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-slate-900">Comptabilité (export Winbiz)</h2>
          <div className="grid grid-cols-2 gap-3">
            <F label="Préfixe factures" k="invoice_prefix" />
            <F label="Préfixe bons" k="order_prefix" />
          </div>
          <F label="Compte tiers par défaut" k="compte_tiers_default" testid="settings-compte-tiers" />
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(MODULES).map(([m, M]) => (
              <div key={m}>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: M.color }}>Compte {M.label}</label>
                <input data-testid={`settings-compte-${m}`} value={s.comptes_comptables[m] ?? ""} onChange={(e) => setCompte(m, e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono2" />
              </div>
            ))}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-violet-600">Compte Refacturation</label>
              <input data-testid="settings-compte-refacturation" value={s.comptes_comptables.refacturation ?? ""} onChange={(e) => setCompte("refacturation", e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono2" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Prochain numéro de facture Winbiz" k="next_winbiz_number" type="number" testid="settings-next-winbiz-number" />
            <F label="Code TVA Winbiz" k="winbiz_vat_code" testid="settings-winbiz-vat-code" />
          </div>
          <p className="text-xs text-slate-400">Le numéro Winbiz (7 chiffres, ex. 2028447) est attribué à chaque facture lors de l'export, puis incrémenté de 1. Il n'apparaît jamais sur le PDF client. Code TVA 300 = prestations au taux normal 8.10 %, méthode nette. L'export se fait dans « Facturation » (bouton Export Winbiz).</p>
        </section>

        <button type="submit" data-testid="settings-save-button"
          className="px-6 py-2.5 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors">
          Enregistrer la configuration
        </button>
      </form>
      <TwoFactorSection />
      <ExportSection />
    </div>
  );
}

